<template>
  <div class="__UsageReports-BudgetSetting">
    <CPageTitle :title="$t('Budget setting')" :description="description" backable @back="onBack" />

    <Panel style="position: relative">
      <CSection separate>
        <div class="__UsageReports-BudgetSetting-HeadContent">
          <div class="__UsageReports-BudgetSetting-HeadContentLeft">
            <CTitle>
              {{ $t('Budget total') }} (<CDate :value="startYearMonth" format="YtoM" /> -
              <CDate :value="endYearMonth" format="YtoM" />)
            </CTitle>

            <div class="__UsageReports-BudgetSetting-TotalAmount">
              <CCurrency :value="totalAmount" type="usd" />
            </div>
          </div>

          <div class="__UsageReports-BudgetSetting-HeadContentRight">
            <CButton
              :label="$t('Set the budget')"
              color="primary"
              :loading="submitLoading"
              @click="onSubmit"
              v-if="showApplyButton"
            />
          </div>
        </div>
      </CSection>

      <CSection class="__UsageReports-BudgetSetting-Conditions">
        <div class="__UsageReports-BudgetSetting-Conditions-Date">
          <Selector
            :label="$t('Starting year')"
            v-model="startYear"
            :options="yearOptions"
            width="240px"
            :use-input="false"
            :disabled="submitLoading || !isAllowedBudgetSettingEdit"
            class="mb-md mr-md"
          />
          <Selector
            :label="$t('Starting month')"
            v-model="startMonth"
            :options="monthOptions"
            width="240px"
            :use-input="false"
            :disabled="submitLoading || !isAllowedBudgetSettingEdit"
            class="mb-md"
          />
        </div>

        <Selector
          :label="$t('Term')"
          v-model="term"
          :options="termOptions"
          width="240px"
          :use-input="false"
          :disabled="submitLoading || !isAllowedBudgetSettingEdit"
          class="mb-md"
        />
      </CSection>

      <CSection margin-bottom="2rem" class="__UsageReports-BudgetSetting-BudgetList">
        <CTitle lighter margin-bottom>
          {{ $t('Monthly budget') }}

          <template v-slot:right-contents>
            <CButton
              :label="$t('Replace with all forecasted budgets')"
              color="primary"
              flat
              size="sm"
              :loading="replaceLoading"
              :disable="submitLoading || !isAllowedBudgetSettingEdit"
              @click="replaceAllValues"
            />
          </template>
        </CTitle>
        <List separator>
          <Item v-for="item in budgets" :key="item.key">
            <ItemSection avatar>
              <ItemLabel class="__UsageReports-BudgetSetting-BudgetInputDate">
                <CDate :value="item.date" format="YtoM" />
              </ItemLabel>
            </ItemSection>

            <ItemSection side>
              <div class="__UsageReports-BudgetSetting-BudgetInput">
                <CInputNumber
                  :value="item.value"
                  :min="0"
                  :debounce="500"
                  hide-bottom-space
                  width="120px"
                  prefix="$"
                  :disabled="submitLoading"
                  @input="onChangeValue($event, item.date)"
                  :readonly="!isAllowedBudgetSettingEdit"
                />

                <template v-if="findForecast(item.date)">
                  <CButton
                    class="__UsageReports-BudgetSetting-BudgetInputReplaceBtn"
                    icon="double_arrow"
                    flat
                    round
                    material
                    :tooltip="$t('Replace with forecasted budget')"
                    :tooltip-delay="500"
                    @click="replaceValue({ date: item.date })"
                    :disable="!isAllowedBudgetSettingEdit"
                  />

                  <div class="__UsageReports-BudgetSetting-BudgetInputForecastAmount">
                    <LabelValue :label="$t('Forecasted')">
                      <template v-slot:value>
                        <CCurrency :value="findForecast(item.date)" type="usd" />
                      </template>
                    </LabelValue>
                  </div>
                </template>
              </div>
            </ItemSection>
          </Item>
        </List>

        <CButton
          :label="$t('Reset all budgets')"
          color="danger"
          flat
          :loading="resetLoading"
          :disable="submitLoading || !isAllowedBudgetSettingEdit"
          @click="resetBudget"
        />
      </CSection>

      <CLoading :loading="fetchLoading" />
    </Panel>
  </div>
</template>

<script>
import { generateDateArray, getMonths } from '@/lib/dates';
import { sleeper } from '@/lib/tools';
import dayjs from 'dayjs';
import Vue from 'vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import DatePicker from '@design-system/components/DatePicker.vue';
import Input from '@design-system/components/Input.vue';
import Item from '@design-system/components/Item.vue';
import ItemLabel from '@design-system/components/ItemLabel.vue';
import ItemSection from '@design-system/components/ItemSection.vue';
import LabelValue from '@design-system/components/LabelValue.vue';
import List from '@design-system/components/List.vue';
import Panel from '@design-system/components/Panel.vue';
import Selector from '@design-system/components/Selector.vue';

import CButton from '@/components/uikit/CButton';
import CCurrency from '@/components/uikit/CCurrency';
import CDate from '@/components/uikit/CDate';
import CInputNumber from '@/components/uikit/CInputNumber';
import CLoading from '@/components/uikit/CLoading';
import CPageTitle from '@/components/uikit/CPageTitle';
import CSection from '@/components/uikit/CSection';
import CTitle from '@/components/uikit/CTitle';
import { roundDown } from '@/lib/numbers';

const messages = {
  en: {
    Budget: 'Budget',
    'Budget setting': 'Budget Settings',
    'Budget total': 'Budgeted Total',
    'Set the budget': 'Apply',
    'Monthly budget': 'Monthly Budget',
    'Replace with all forecasted budgets': 'Replace all forecasted amounts',
    'Replace with forecasted budget': 'Replace all forecasted amounts',
    Forecasted: 'Forecasted',
    'Reset all budgets': 'Reset',
    Term: 'Term',
    '3 months': '3 months',
    '6 months': '6 months',
    '12 months': '12 months',
    'Starting month': 'Starting Month',
    'Starting year': 'Starting Year',
  },
  ja: {
    Budget: '予算',
    'Budget setting': '予算の設定',
    'Budget total': '予算の合計',
    'Set the budget': '予算を確定する',
    'Monthly budget': '各月の予算',
    'Replace with all forecasted budgets': 'すべての予測値を代入',
    'Replace with forecasted budget': '予測値を代入する',
    Forecasted: '予測値',
    'Reset all budgets': '予算をリセット',
    Term: '予算の期間',
    '3 months': '3ヶ月',
    '6 months': '6ヶ月',
    '12 months': '12ヶ月',
    'Starting month': '開始月',
    'Starting year': '開始年',
  },
};

export default Vue.extend({
  name: 'BudgetSetting',
  i18n: { messages },
  components: {
    Panel,
    Selector,
    DatePicker,
    List,
    Item,
    ItemSection,
    ItemLabel,
    Input,
    LabelValue,

    CPageTitle,
    CSection,
    CTitle,
    CInputNumber,
    CCurrency,
    CButton,
    CDate,
    CLoading,
  },
  props: {
    id: String,
  },
  data() {
    return {
      term: 3,
      termOptions: [
        {
          label: this.$t('3 months'),
          value: 3,
        },
        {
          label: this.$t('6 months'),
          value: 6,
        },
        {
          label: this.$t('12 months'),
          value: 12,
        },
      ],

      startMonth: null,
      // endMonth is in computed.
      startYear: null,

      // Loading
      replaceLoading: false,
      resetLoading: false,
      submitLoading: false,
    };
  },
  async created() {
    await this.fetch();
    this.reset();
  },
  watch: {
    term() {
      this.updateStored({ start: `${this.startYear}/${this.startMonth}`, term: this.term });
    },
    startMonth() {
      this.updateStored({ start: `${this.startYear}/${this.startMonth}`, term: this.term });
    },
    startYear() {
      this.updateStored({ start: `${this.startYear}/${this.startMonth}`, term: this.term });
      this.fetchBudget({ force: true, year: this.startYear });
    },
  },
  computed: {
    ...mapState('accounts', {
      accountId: state => state.selectedId,
    }),

    ...mapState('budget', {
      fetchLoading: state => state.loading.fetch,
    }),

    ...mapGetters('budget', ['getStoredBudget', 'getBudgetForecast']),
    ...mapGetters('rbac', {
      isResourceAllowed: 'isResourceAllowed',
    }),

    description() {
      return this.id;
    },

    yearOptions() {
      const start = dayjs()
        .subtract(1, 'year')
        .toDate();
      const end = dayjs()
        .add(1, 'year')
        .toDate();
      return generateDateArray(start, end, 'years', 'YYYY');
    },

    monthOptions() {
      return getMonths();
    },

    budgets() {
      return this.getStoredBudget;
    },

    startYearMonth() {
      return dayjs(`${this.startYear}/${this.startMonth}`, 'YYYY/MM').format();
    },

    endYearMonth() {
      return dayjs(`${this.startYear}/${this.startMonth}`, 'YYYY/MM')
        .add(this.term - 1, 'month')
        .format();
    },

    totalAmount() {
      return this.budgets.reduce((acc, cur) => {
        let current = cur.value ? cur.value : 0;

        return acc + current;
      }, 0);
    },

    showApplyButton() {
      return (
        this.$can('ModifyAccountSettings') &&
        this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.accountId)
      );
    },

    isAllowedBudgetSettingEdit() {
      return (
        this.$can('ModifyAccountSettings') &&
        this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.accountId)
      );
    },
  },
  methods: {
    ...mapMutations('budget', ['updateStored', 'updateStoredValue']),
    ...mapActions('budget', ['fetchBudget', 'updateBudget', 'InitializeStoredBudget']),
    ...mapActions('costForecast', ['fetchForecastMonthly', 'fetchForecastMonthToDate']),

    reset() {
      this.startMonth = this.getStoredBudget[0]
        ? dayjs(this.getStoredBudget[0].date).format('MM')
        : dayjs().format('MM');

      this.startYear = this.getStoredBudget[0]
        ? dayjs(this.getStoredBudget[0].date).format('YYYY')
        : dayjs().format('YYYY');
    },

    findForecast(date) {
      const target = this.getBudgetForecast.find(i => {
        return dayjs(i.date).isSame(date, 'month');
      });

      if (!target) return 0;

      return target.forecast ? roundDown(target.forecast) : 0;
    },

    onChangeValue(evt, date) {
      this.updateStoredValue({ date: date, value: evt });
    },

    onBack() {
      this.$router.back();
    },

    async onSubmit() {
      try {
        this.submitLoading = true;
        await sleeper(1000);
        await this.updateBudget({ year: this.startYear });

        // Update forecast data.
        await this.fetchForecastMonthly({ force: true });
        await this.fetchForecastMonthToDate({ force: true });

        await this.$alpha.notify.success('update', this.$t('Budget'));
      } catch (e) {
        console.log(e);
        await this.$alpha.notify.error('update', this.$t('Budget'));
      } finally {
        this.submitLoading = false;
      }
    },

    async fetch() {
      // Set current budget to stored budget in vuex. (Initialize)
      this.InitializeStoredBudget();

      // Set start month
      this.startMonth = this.getStoredBudget.length
        ? dayjs(this.getStoredBudget[0].date).format('MM')
        : dayjs().format('MM');

      this.startYear = this.getStoredBudget[0]
        ? dayjs(this.getStoredBudget[0].date).format('YYYY')
        : dayjs().format('YYYY');

      // Set term
      this.term = this.getStoredBudget.length ? this.getStoredBudget.length : 3;
    },

    replaceValue({ date }) {
      this.updateStoredValue({ date: date, value: this.findForecast(date) });
    },

    async replaceAllValues() {
      this.replaceLoading = true;
      await sleeper(1000);

      this.budgets.forEach(i => {
        this.updateStoredValue({ date: i.date, value: this.findForecast(i.date) });
      });

      this.replaceLoading = false;
    },

    async resetBudget() {
      this.resetLoading = true;
      await sleeper(1000);

      this.budgets.forEach(i => {
        this.updateStoredValue({ date: i.date, value: 0 });
      });

      this.resetLoading = false;
    },
  },
});
</script>

<style lang="scss" scoped>
.__UsageReports-BudgetSetting {
  padding: 0 $space-sm;

  &-HeadContent {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &-TotalAmount {
    font-size: $font-size-xl;
  }

  &-Conditions {
    border-bottom: 1px dotted $border-color;
    padding-bottom: $space-xs;

    &-Date {
      display: flex;
    }
  }

  &-BudgetList {
    max-width: 480px;
  }

  &-BudgetInput {
    display: flex;
  }

  &-BudgetInputDate {
    width: 150px;
  }

  &-BudgetInputReplaceBtn {
    transform: rotate(180deg);
    margin: 0 $space-md;
  }

  &-Footer {
    border-top: 1px solid $border-color;
    padding-top: $space-md;
  }
}
</style>
